import { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  IconButton,
  InputAdornment,
  Button,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Visibility, VisibilityOff ,Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { endpoints } from "../../Utils/UrlEnum";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateData } from "../../Services/updateData";
import { FormsIds } from "../../Utils/Constants";
import * as yup from "yup";

import GenericModal from "../GenericModal";
import genericStyles from "../../Styles/genericStyles.module.css";

const MyAccountValidationSchema = yup.object().shape({
  username: yup.string().required(Vocabulary.requiredField),
  email: yup
    .string()
    .email(Vocabulary.invalidEmail)
    .required(Vocabulary.requiredField),
  password: yup
    .string()
    .nullable()
    .notRequired(),
  confirmPassword: yup
    .string()
    .nullable()
    .notRequired()
    .oneOf([yup.ref("password")], Vocabulary.passwordsMustMatch),
});

export interface MyAccountModalProps {
  open: boolean;
  onClose: () => void;
  userData: any;
}

export default function MyAccountModal(props: MyAccountModalProps) {
  const { open, onClose, userData } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, handleSubmit, formState, reset } = useForm({
    resolver: yupResolver(MyAccountValidationSchema),
    mode: "onChange",
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  /**
   * Reset form with user data when modal opens or userData changes
   */
  useEffect(() => {
    if (userData && open) {
      reset({
        username: userData.username || "",
        email: userData.email || "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [userData, open, reset]);

  /**
   * Update user profile
   */
  const onSubmit = async (data: any) => {
    const url = `${endpoints.user.main}/${userData?._id}`;
    
    const res = (await updateData(url, data)) as any;
    
    if (!res || res?.error) {
      return;
    }

    // Update localStorage
    localStorage.setItem("userName", data.username);
    
    // Close modal and reset form
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={Vocabulary.myAccount}
      children={
        <form
          id={FormsIds.myAccountForm}
          onSubmit={handleSubmit(onSubmit)}
          style={{ padding: "20px 0" }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                {...register("username")}
                label={Vocabulary.username}
                fullWidth
                variant="outlined"
                error={!!formState.errors.username}
                helperText={formState.errors.username?.message?.toString()}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                {...register("email")}
                label={Vocabulary.email}
                fullWidth
                variant="outlined"
                type="email"
                error={!!formState.errors.email}
                helperText={formState.errors.email?.message?.toString()}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                {...register("password")}
                label={Vocabulary.newPassword}
                fullWidth
                variant="outlined"
                type={showPassword ? "text" : "password"}
                error={!!formState.errors.password}
                helperText={formState.errors.password?.message?.toString()}
                placeholder={Vocabulary.leaveEmptyToKeepCurrent}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                {...register("confirmPassword")}
                label={Vocabulary.confirmPassword}
                fullWidth
                variant="outlined"
                type={showConfirmPassword ? "text" : "password"}
                error={!!formState.errors.confirmPassword}
                helperText={formState.errors.confirmPassword?.message?.toString()}
                placeholder={Vocabulary.leaveEmptyToKeepCurrent}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </form>
      }
      actions={
        <div className={genericStyles.editClientModalEditButton}>
          <Button
            color="error"
            variant="contained"
            onClick={handleClose}
            startIcon={<CancelIcon />}
          >
            {Vocabulary.cancel}
          </Button>
          <Button
            variant="contained"
            type="submit"
            form={FormsIds.myAccountForm}
            color="success"
            startIcon={<SaveIcon />}
          >
            {Vocabulary.save}
          </Button>
        </div>
      }
    />
  );
}
